import { cx } from "@emotion/css";
import LinearProgress from "@mui/material/LinearProgress";
import {
	Clock,
	Heart,
	type LucideIcon,
	ThumbsDown,
	ThumbsUp,
} from "lucide-react";
import type { ReactNode } from "react";
import type { useTranslation } from "react-i18next";
import { type DeckStats, DeckType } from "src/api/flashcardDecks";
import {
	type DeckChartSegment,
	DeckCircleChart,
} from "src/components/Statistics/flashcards/DeckCircleChart";

type TranslateFn = ReturnType<typeof useTranslation>["t"];

interface DeckStatsContent {
	chart: ReactNode;
	statsList: ReactNode;
}

interface GradeConfig {
	grade: number;
	label: string;
	Icon: LucideIcon;
	color: string;
	tintClass: string;
	scoreClassName: string;
}

interface DeckStatRowProps {
	config: Pick<GradeConfig, "Icon" | "label" | "tintClass" | "scoreClassName">;
	count: number;
	total: number;
	testId?: string;
}

export function DeckStatRow({
	config,
	count,
	total,
	testId,
}: DeckStatRowProps) {
	const { Icon, label, tintClass, scoreClassName } = config;
	const percent = total > 0 ? (count / total) * 100 : 0;

	return (
		<div
			className={cx("flex items-center gap-12", tintClass)}
			data-test-id={testId}
		>
			<span className="tinted-tile">
				<Icon className="icon-small" />
			</span>
			<div className="flex flex-col gap-8 flex-1 min-w-0">
				<div className="flex justify-between items-center gap-8">
					<span className="size-16">{label}</span>
					<span className={cx("size-20 font-semibold", scoreClassName)}>
						{count}
					</span>
				</div>
				<LinearProgress
					variant="determinate"
					value={percent}
					className="progress-thin"
				/>
			</div>
		</div>
	);
}

function getGradeConfigs(type: DeckType, t: TranslateFn): GradeConfig[] {
	if (type === DeckType.KnowDontKnow) {
		return [
			{
				grade: 1,
				label: t("decks.knowDontKnow.know", "Znam"),
				Icon: ThumbsUp,
				color: "var(--green)",
				tintClass: "tint-success",
				scoreClassName: "text-success",
			},
			{
				grade: 0,
				label: t("decks.knowDontKnow.dontKnow", "Nie znam"),
				Icon: ThumbsDown,
				color: "var(--red)",
				tintClass: "tint-error",
				scoreClassName: "text-error",
			},
		];
	}

	return [
		{
			grade: 3,
			label: t("decks.gradeButtons.3", "Proste"),
			Icon: Heart,
			color: "var(--primaryBlue)",
			tintClass: "tint-info",
			scoreClassName: "text-primary",
		},
		{
			grade: 2,
			label: t("decks.gradeButtons.2", "Dobrze"),
			Icon: ThumbsUp,
			color: "var(--green)",
			tintClass: "tint-success",
			scoreClassName: "text-success",
		},
		{
			grade: 1,
			label: t("decks.gradeButtons.1", "Słabo"),
			Icon: ThumbsDown,
			color: "var(--yellow)",
			tintClass: "tint-warning",
			scoreClassName: "text-warning",
		},
		{
			grade: 0,
			label: t("decks.gradeButtons.0", "Powtórz"),
			Icon: Clock,
			color: "var(--red)",
			tintClass: "tint-error",
			scoreClassName: "text-error",
		},
	];
}

export function buildDeckStatsContent(
	type: DeckType,
	stats: DeckStats,
	t: TranslateFn,
): DeckStatsContent {
	const grades = getGradeConfigs(type, t).map((config) => ({
		config,
		count: stats.counts?.[String(config.grade)] ?? 0,
	}));

	const segments: DeckChartSegment[] = grades.map(({ config, count }) => ({
		count,
		color: config.color,
	}));

	return {
		chart: (
			<DeckCircleChart
				className="circle-chart--large"
				segments={segments}
				total={stats.total}
			/>
		),
		statsList: grades.map(({ config, count }) => (
			<DeckStatRow
				key={config.grade}
				config={config}
				count={count}
				total={stats.total}
			/>
		)),
	};
}
